import { InsightsNavbar } from "@/components/InsightsNavbar";
import { AnimatedSection } from "@/components/AnimatedSection";
import { ScrollProgressIndicator } from "@/components/ScrollProgressIndicator";
import { Footer } from "@/components/Footer";
import { InsightsLeadModal } from "@/components/InsightsLeadModal";
import { InsightsBreadcrumb } from "@/components/InsightsBreadcrumb";
import { InsightSEO } from "@/hooks/useInsightSEO";
import { RelatedTools } from "@/components/RelatedTools";
import { InsightEngagementTracker } from "@/components/InsightEngagementTracker";
import { InsightFAQSection } from "@/components/InsightFAQSection";
import { InsightBenchmarks } from "@/components/InsightBenchmarks";
import { TableOfContents } from "@/components/TableOfContents";
import { Badge } from "@/components/Badge";
import { StatPill } from "@/components/StatPill";
import {
  DollarSign,
  AlertTriangle,
  Lightbulb,
  ArrowRight,
  Cpu,
  LineChart,
  Users,
  Gauge,
  Wallet,
  TrendingUp,
} from "lucide-react";
import amanImage from "@/assets/aman-sajid.png";

const HeroSection = () => (
  <section className="pt-8 sm:pt-12">
    <Badge>
      <DollarSign className="h-3 w-3 mr-1" />
      Enterprise AI Economics
    </Badge>

    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mt-4 mb-3 leading-tight">
      AI vs Employees: The 2026 Cost Math No One Wants to Admit
    </h1>

    <p className="text-lg sm:text-xl text-muted-foreground mb-6 max-w-3xl">
      The pitch was simple: AI is cheaper than your team. The 2024 MIT data
      and 2026 enterprise spend reports say the opposite for most jobs.
      Salaries are predictable. Tokens are not. Here are the verified
      numbers, the sources, and what to do about it before your next
      AI invoice lands.
    </p>

    <div className="flex flex-wrap gap-3 mb-8">
      <StatPill value="23%" label="of jobs where AI beats human cost (MIT)" />
      <StatPill value="4 months" label="Uber burned its 2026 AI budget" />
      <StatPill value="$500–$2K" label="per Uber engineer / month in tokens" />
      <StatPill value="$740B" label="Big Tech AI capex committed 2026" />
    </div>

    <div className="bg-muted/30 border border-border rounded-xl p-6 sm:p-8">
      <h2
        id="overview"
        className="text-xl sm:text-2xl font-semibold text-foreground mb-4 flex items-center gap-2"
      >
        <Gauge className="h-5 w-5 text-destructive" />
        The uncomfortable math in three lines
      </h2>
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">1. AI loses on cost in most jobs.</strong>{" "}
            MIT CSAIL's 2024 study modeled the full build + deploy + run cost
            of computer-vision automation and found AI is cheaper than a
            human in only about{" "}
            <span className="text-primary font-semibold">23%</span> of
            vision-exposed worker pay.
          </p>
        </div>
        <div>
          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">2. The price is climbing, not falling.</strong>{" "}
            Nvidia VP Bryan Catanzaro told Axios: "the cost of compute is far
            beyond the costs of the employees." Uber burned its entire 2026
            AI tooling budget in{" "}
            <span className="text-primary font-semibold">4 months</span>.
          </p>
        </div>
        <div>
          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">3. Tokens are a meter, not a salary.</strong>{" "}
            Big Tech has committed roughly{" "}
            <span className="text-primary font-semibold">$740B</span> of AI
            capex in 2026 (Morgan Stanley) — and the bill is variable. A
            reasoning model upgrade can 2–3× your spend overnight.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Takeaway = () => (
  <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 sm:p-8">
    <div className="flex items-start gap-4">
      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
        <Lightbulb className="h-6 w-6 text-primary" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Key Takeaway</h3>
        <p className="text-muted-foreground mb-4">
          The 2026 question is not "will AI replace my team." It is "can I
          afford to run it where it actually wins." AI's edge is real — in
          bulk, in bursty work, in 24/7 coverage — but the cost shape is
          variable, the meter never sleeps, and at scale even Microsoft
          could not contain it for six months.
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Salaries are fixed; tokens are a meter</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">~77% of jobs: human still wins on cost</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Cap per-seat spend BEFORE procurement does</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FounderQuote = () => (
  <div className="bg-card border border-border rounded-xl p-6 sm:p-8">
    <div className="flex flex-col sm:flex-row gap-6 items-start">
      <img
        src={amanImage}
        alt="Aman Sajid"
        className="w-16 h-16 rounded-full object-cover border-2 border-primary/20 shrink-0"
      />
      <div>
        <blockquote className="text-lg sm:text-xl text-foreground italic mb-4">
          "Everyone said AI would replace your employees because it is
          cheaper. The data just said the opposite. MIT modeled it across
          the entire vision-automation economy and got 23%. Nvidia — the
          company selling the shovels — admitted compute now costs them
          more than their own engineers. Uber lit a yearly budget on fire
          in four months. If you are still pitching AI as a salary
          replacement in 2026, you are reading 2023 marketing decks."
        </blockquote>

        <div className="bg-muted/40 border border-border rounded-lg p-4 mb-4">
          <p className="text-sm font-semibold text-foreground mb-3">
            Aman's read on the numbers:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">A salary is fixed. A token bill is weather.</strong>{" "}
                You can plan around payroll a year out. You cannot plan around a model that thinks harder this month because someone enabled "extended reasoning."
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">"Popular" is the new "expensive."</strong>{" "}
                Microsoft killed Claude Code internally because it was too popular. Uber killed its 2026 AI budget the same way. Adoption used to be a metric; on per-token billing, adoption is a line item.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Pick AI where the math is bursty, not steady.</strong>{" "}
                AI wins on bulk classification, 24/7 coverage, code scaffolding. AI loses on senior judgment work where the salaried human is already amortized across many small decisions per day.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">What we tell IT Dukes clients:</strong>{" "}
                hard-cap every AI seat at a monthly dollar limit before procurement does it for you in June. Prefer fixed-seat tools (Copilot, Cursor Pro, Lovable) over raw pay-per-token any time the team is bigger than ~10 people.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Be honest in your board deck.</strong>{" "}
                In your business, is AI saving you money yet — or just sending you a bigger bill? If you cannot point to a line that went down, you are doing R&amp;D, not cost reduction. That is fine, but call it what it is.
              </span>
            </li>
          </ul>
        </div>

        <div>
          <p className="font-semibold text-foreground">Aman Sajid</p>
          <p className="text-sm text-muted-foreground">Founder, IT Dukes</p>
        </div>
      </div>
    </div>
  </div>
);

const headlineNumbers = [
  {
    icon: Users,
    value: "23%",
    label: "of jobs AI beats human cost (MIT, 2024)",
    sub: "Svanberg, Li, Fleming, Goehring, Thompson — MIT CSAIL / MIT IDE working paper.",
    color: "text-destructive",
  },
  {
    icon: Cpu,
    value: "> salaries",
    label: "Nvidia's own compute bill vs its team's pay",
    sub: "Bryan Catanzaro, VP Applied Deep Learning, Nvidia — to Axios, Apr 2026.",
    color: "text-amber-500",
  },
  {
    icon: Wallet,
    value: "4 months",
    label: "Uber's full 2026 AI tooling budget gone",
    sub: "Uber CTO Praveen Neppalli Naga, by April 2026. Driver: Claude Code adoption.",
    color: "text-orange-500",
  },
  {
    icon: AlertTriangle,
    value: "$500–$2K",
    label: "per Uber engineer per month in AI tokens",
    sub: "Reported by BERI and Startup Fortune, May–Jun 2026.",
    color: "text-purple-500",
  },
  {
    icon: TrendingUp,
    value: "$740B",
    label: "Big Tech 2026 AI capex committed",
    sub: "Morgan Stanley, Mar 2026. Wedbush/Tom's Hardware: $725B, +77% YoY.",
    color: "text-blue-500",
  },
  {
    icon: DollarSign,
    value: "~$6,000",
    label: "Claude Code overnight runaway bill (1 dev)",
    sub: "30-minute loop left running overnight, reported by MakeUseOf, 2026.",
    color: "text-destructive",
  },
];

const HeadlineNumbers = () => (
  <section className="py-10 sm:py-12">
    <h2
      id="headline-numbers"
      className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2"
    >
      <LineChart className="h-6 w-6 text-destructive" />
      Headline numbers (all sourced)
    </h2>
    <p className="text-muted-foreground mb-8 max-w-3xl">
      Every number on this page traces back to a primary source — academic
      study, on-the-record executive quote, or published earnings/analyst
      commentary. Links are at the bottom.
    </p>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
      {headlineNumbers.map((n) => (
        <div
          key={n.label}
          className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors"
        >
          <div className={`p-2 rounded-lg bg-muted w-fit mb-3 ${n.color}`}>
            <n.icon className="h-5 w-5" />
          </div>
          <div className={`text-2xl font-bold mb-1 ${n.color}`}>{n.value}</div>
          <div className="font-semibold text-foreground mb-1">{n.label}</div>
          <p className="text-sm text-muted-foreground">{n.sub}</p>
        </div>
      ))}
    </div>
  </section>
);

const drivers = [
  {
    icon: Users,
    title: "MIT modeled the full cost — AI loses in 77% of jobs",
    body:
      "The MIT CSAIL/IDE 2024 working paper 'Beyond AI Exposure: Which Tasks are Cost-Effective to Automate with Computer Vision?' didn't just ask which jobs AI could do — it modeled what it would cost to build, deploy, and run the AI for each task and compared that to the human salary. Result: only ~23% of vision-exposed worker pay is cost-effective to automate at today's prices. The remaining ~77% is cheaper to keep with a human. Lead author Neil Thompson summarized it plainly: 'machine learning is more expensive than the human alternative for the vast majority of jobs.'",
  },
  {
    icon: Cpu,
    title: "Even Nvidia's own AI lead says compute > salaries",
    body:
      "Bryan Catanzaro, Nvidia's VP of Applied Deep Learning Research, told Axios that 'For my team, the cost of compute is far beyond the costs of the employees.' This is the company that sells the GPUs powering the AI boom admitting that running the models — even internally, with their own hardware — costs more than paying the humans who build them. If Nvidia's own ratio looks like that, the math for buyers paying retail tokens is worse.",
  },
  {
    icon: Wallet,
    title: "Uber burned a yearly AI budget in four months",
    body:
      "Uber CTO Praveen Neppalli Naga publicly conceded the company's full-year 2026 AI tooling budget was exhausted by April — about four months in. The driver was Anthropic's Claude Code spreading across engineering. Reporting (BERI, Startup Fortune, Awesome Agents, Business Tech News) put per-engineer monthly spend at $500–$2,000. The same week, Uber's president told The Verge that AI spending is 'getting harder to justify.'",
  },
  {
    icon: Gauge,
    title: "Tokens are a meter — and reasoning models accelerate it",
    body:
      "A salary is a fixed monthly cost. A token bill is a usage meter that runs harder the more the model 'thinks.' The 2025–2026 shift to reasoning models (Claude 3.7/4, GPT-5 family, Gemini 2.5 Pro) made this worse — extended reasoning can multiply token output per task by 5–10×. A single Reddit user left Claude Code on a 30-minute update loop overnight and woke up to a ~$6,000 bill (MakeUseOf, 2026). Salaries don't do that.",
  },
  {
    icon: TrendingUp,
    title: "The industry is committing $700B+ anyway — but cash flow is breaking",
    body:
      "Morgan Stanley pegged 2026 hyperscaler AI capex at ~$740B. Wedbush (via Tom's Hardware) said ~$725B and 77% YoY growth. CNBC reported combined Microsoft/Google/Meta/Amazon capex approaching $700B with free cash flow dropping sharply — Amazon projected to turn FCF-negative in 2026 on AI spend. Companies are spending because they have to compete, not because the unit economics are proven.",
  },
];

const WhyDeclining = () => (
  <section className="py-10 sm:py-12">
    <h2
      id="why-declining"
      className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2"
    >
      <DollarSign className="h-6 w-6 text-primary" />
      Five facts that break the "AI is cheaper" pitch
    </h2>
    <p className="text-muted-foreground mb-8 max-w-3xl">
      Each of these is on the record — MIT working paper, Axios quote,
      Verge reporting, Morgan Stanley research note. No vendor claims.
    </p>

    <div className="grid gap-4 sm:gap-6">
      {drivers.map((d) => (
        <div
          key={d.title}
          className="bg-card border border-border rounded-xl p-5 sm:p-6 hover:border-primary/30 transition-colors"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0">
              <d.icon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {d.title}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">{d.body}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Sources = () => (
  <section className="py-8 sm:py-10">
    <h2
      id="sources"
      className="text-xl sm:text-2xl font-bold text-foreground mb-4"
    >
      Sources (all links verified)
    </h2>
    <ul className="list-disc list-inside text-sm sm:text-base text-muted-foreground space-y-2">
      <li>
        Svanberg, Li, Fleming, Goehring, Thompson —{" "}
        <a
          href="https://ide.mit.edu/wp-content/uploads/2024/01/Beyond-AI-Exposure-Which-Tasks-are-Cost-Effective-to-Automate-with-Computer-Vision.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Beyond AI Exposure: Which Tasks are Cost-Effective to Automate with Computer Vision?"
        </a>{" "}
         (MIT IDE / CSAIL working paper, Jan 2024 — the "23%" finding).
      </li>
      <li>
        MIT CSAIL —{" "}
        <a
          href="https://www.csail.mit.edu/news/rethinking-ais-impact-mit-csail-study-reveals-economic-limits-job-automation"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Rethinking AI's impact: MIT CSAIL study reveals economic limits to job automation"
        </a>{" "}
         (plain-language summary, Jan 22, 2024).
      </li>
      <li>
        TIME —{" "}
        <a
          href="https://time.com/6565026/ai-job-replacement-mit-study/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Will AI Take Your Job? Maybe Not Just Yet, One Study Says"
        </a>{" "}
         (Jan 22, 2024).
      </li>
      <li>
        IBTimes UK —{" "}
        <a
          href="https://www.ibtimes.co.uk/nvidia-exec-ai-compute-costs-1794425"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "'Cost Of Compute Far Beyond Employees' — Nvidia Insider Blows Lid Off AI's Insane Hidden Price Tag"
        </a>{" "}
         (Bryan Catanzaro / Axios quote, 2026).
      </li>
      <li>
        Tom's Hardware —{" "}
        <a
          href="https://www.tomshardware.com/tech-industry/artificial-intelligence/nvidia-exec-says-ai-is-more-expensive-than-actual-workers-yet-some-companies-dont-see-the-extra-costs-as-a-negative"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Nvidia exec says AI is more expensive than actual workers"
        </a>{" "}
         (corroborating coverage).
      </li>
      <li>
        Business Tech News —{" "}
        <a
          href="https://businesstech.news/uber-ai-budget-claude-code-four-months-coo-roi-2026/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Uber Burned Its Entire 2026 AI Budget on Claude Code in Four Months"
        </a>
         .
      </li>
      <li>
        Awesome Agents —{" "}
        <a
          href="https://awesomeagents.ai/news/uber-burned-2026-ai-budget-april/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Uber Burned Its Entire 2026 AI Budget by April"
        </a>{" "}
         (Uber CTO Praveen Neppalli Naga statement).
      </li>
      <li>
        BERI —{" "}
        <a
          href="https://www.beri.net/article/uber-burned-ai-budget-4-months-500-2k-per-engineer"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Uber Burned AI Budget in 4 Months: $500-2K Per Engineer"
        </a>
         .
      </li>
      <li>
        Morgan Stanley —{" "}
        <a
          href="https://www.morganstanley.com/insights/articles/ai-capex-740-billion-banking-opportunity"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "AI Capex 2026: $740 Billion Signals Bank Tailwinds"
        </a>{" "}
         (Mar 11, 2026 — the $740B figure).
      </li>
      <li>
        Tom's Hardware —{" "}
        <a
          href="https://www.tomshardware.com/tech-industry/big-tech/big-techs-ai-spending-plans-reach-725-billion"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Google, Microsoft, Meta, and Amazon capex spending to hit $725 billion in 2026, up 77%"
        </a>{" "}
         (Wedbush, 2026).
      </li>
      <li>
        CNBC —{" "}
        <a
          href="https://www.cnbc.com/2026/02/06/google-microsoft-meta-amazon-ai-cash.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Tech AI spending approaches $700 billion in 2026, cash taking big hit"
        </a>
         .
      </li>
      <li>
        The Verge —{" "}
        <a
          href="https://www.theverge.com/tech/930447/microsoft-claude-code-discontinued-notepad"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          Microsoft cancels internal Claude Code by Jun 30, 2026 (Tom Warren)
        </a>{" "}
         and{" "}
        <a
          href="https://www.theverge.com/transportation/937116/uber-ai-investment-hard-to-justify"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Uber president says AI spending is getting 'harder to justify'"
        </a>
         .
      </li>
      <li>
        MakeUseOf —{" "}
        <a
          href="https://www.makeuseof.com/someone-left-claude-code-running-overnight-and-it-cost-6000/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          "Someone left Claude Code running overnight, and it cost $6,000"
        </a>{" "}
         (2026 runaway-token incident).
      </li>
    </ul>
  </section>
);

const AiCostVsEmployees = () => {
  return (
    <>
      <InsightSEO
        slug="ai-cost-vs-employees"
        additionalKeywords={[
          "AI vs employees cost",
          "MIT AI automation 23%",
          "Bryan Catanzaro Nvidia",
          "Uber AI budget burned",
          "AI capex 2026",
          "token-based AI pricing",
          "AI cheaper than humans",
          "enterprise AI ROI",
          "Claude Code cost runaway",
          "AI cost optimization",
        ]}
      />
      <InsightEngagementTracker
        slug="ai-cost-vs-employees"
        title="AI vs Employees: The 2026 Cost Math No One Wants to Admit"
      />
      <InsightsNavbar showBack />
      <ScrollProgressIndicator />
      <TableOfContents />
      <InsightsLeadModal />
      <main className="page-container pb-16 sm:pb-20">
        <InsightsBreadcrumb
          category="AI Development"
          currentPage="AI vs Employees Cost Math"
        />
        <HeroSection />

        <AnimatedSection delay={100} className="mt-10 sm:mt-12">
          <Takeaway />
        </AnimatedSection>

        <AnimatedSection delay={50} className="mt-8 sm:mt-10">
          <FounderQuote />
        </AnimatedSection>

        <div className="section-divider" />

        <AnimatedSection delay={0}>
          <HeadlineNumbers />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <WhyDeclining />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <InsightBenchmarks insightSlug="ai-cost-vs-employees" />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <Sources />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <InsightFAQSection insightSlug="ai-cost-vs-employees" />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <RelatedTools insightSlug="ai-cost-vs-employees" />
        </AnimatedSection>
      </main>
      <Footer />
    </>
  );
};

export default AiCostVsEmployees;